import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Printer, ShieldCheck } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useCompanyConfig } from "../../../hooks/useCompanyConfig";

/* ====== Theme helpers (ProductDetail ile aynı yaklaşım) ====== */
const hexToRgb = (hex: string) => {
  const h = (hex || "#ffffff").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

type Row = {
  urun: string;
  adet: string;
  birim: string;
  araToplam: string;
};

const MesafeliSatisSozlesmesi: React.FC = () => {
  const { theme } = useTheme();
  const company = useCompanyConfig();

  const accent = theme?.accent ?? "#2563eb";
  const textPrimary = theme?.textPrimary ?? "#0f172a";
  const textSecondary = theme?.textSecondary ?? "#475569";

  const pageBg = theme?.backgroundColor || "#ffffff";
  const bgRgb = hexToRgb(pageBg);
  const isDarkBg = luminance(bgRgb) < 0.5;

  const cardBg = isDarkBg ? "rgba(17,24,39,0.92)" : "#ffffff";
  const borderCol = isDarkBg ? "rgba(255,255,255,.14)" : "rgba(15,23,42,.12)";
  const softShadow = isDarkBg
    ? "0 10px 26px rgba(0,0,0,.35)"
    : "0 10px 26px rgba(2,6,23,.08)";

  const cardStyle: React.CSSProperties = {
    backgroundColor: cardBg,
    borderColor: borderCol,
    borderWidth: 1,
    boxShadow: softShadow,
  };
  const subtleBorder: React.CSSProperties = { borderColor: borderCol };

  const rows: Row[] = useMemo(
    () => [
      { urun: "[Ürün 1 Adı]", adet: "[Adet]", birim: "[Fiyat] TL", araToplam: "[Toplam] TL" },
      { urun: "[Ürün 2 Adı]", adet: "[Adet]", birim: "[Fiyat] TL", araToplam: "[Toplam] TL" },
    ],
    []
  );

  const onPrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <div style={{ backgroundColor: pageBg }}>
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page { padding: 0 !important; }
          .print-card { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
          a { text-decoration: none !important; color: inherit !important; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto px-4 py-6 md:py-10 print-page">
        {/* Top bar */}
        <div className="no-print mb-4 md:mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold"
              style={{ ...subtleBorder, color: textPrimary, backgroundColor: cardBg }}
            >
              <ArrowLeft className="w-4 h-4" />
              Anasayfa
            </Link>

            <div className="hidden md:flex items-center gap-2 text-sm" style={{ color: textSecondary }}>
              <span className="opacity-70">/</span>
              <span>Mesafeli Satış Sözleşmesi</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onPrint}
              className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold"
              style={{ ...subtleBorder, color: textPrimary, backgroundColor: cardBg }}
            >
              <Printer className="w-4 h-4" />
              Yazdır
            </button>
          </div>
        </div>

        {/* Header */}
        <div className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1 border"
                   style={{ ...subtleBorder, color: textSecondary, backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "#fff" }}>
                <FileText className="w-4 h-4" />
                Yasal Metin
              </div>

              <h1 className="mt-3 text-2xl md:text-3xl font-extrabold leading-tight" style={{ color: textPrimary }}>
                MESAFELİ SATIŞ SÖZLEŞMESİ
              </h1>

              <p className="mt-2 text-sm leading-6" style={{ color: textSecondary }}>
                Bu metin, <b style={{ color: textPrimary }}>{company.website}</b> üzerinden yapılan satışlar için
                mesafeli satış sözleşmesi hükümlerini içerir.
              </p>
            </div>

            <div className="hidden md:flex items-center gap-2 text-xs font-semibold rounded-xl px-3 py-2 border"
                 style={{ ...subtleBorder, color: textSecondary, backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "#fff" }}>
              <ShieldCheck className="w-4 h-4" style={{ color: textPrimary }} />
              Sipariş onayı ile kabul edilir
            </div>
          </div>

          <div className="mt-5 border-t" style={subtleBorder} />

          {/* Quick nav */}
          <div className="no-print mt-4 flex flex-wrap gap-2 text-xs">
            {[
              { id: "m1", label: "1. Taraflar" },
              { id: "m2", label: "2. Tanımlar" },
              { id: "m3", label: "3. Konu" },
              { id: "m7", label: "7. Ürün/Ödeme" },
              { id: "m9", label: "9. Genel Hükümler" },
              { id: "m10", label: "10. Cayma" },
              { id: "m11", label: "11. Cayma Kullanılamaz" },
              { id: "m13", label: "13. Yetki" },
            ].map((x) => (
              <a
                key={x.id}
                href={`#${x.id}`}
                className="rounded-full border px-3 py-1.5 hover:opacity-90 transition"
                style={{
                  borderColor: borderCol,
                  color: textSecondary,
                  backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "#fff",
                }}
              >
                {x.label}
              </a>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="mt-6 space-y-6">
          {/* 1 */}
          <section id="m1" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              1. TARAFLAR
            </h2>
            <p className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              İşbu Sözleşme aşağıdaki taraflar arasında aşağıda belirtilen hüküm ve şartlar çerçevesinde imzalanmıştır.
            </p>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  SATICI <span className="text-xs font-semibold" style={{ color: textSecondary }}>(“SATICI”)</span>
                </div>
                <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
                  <div><b style={{ color: textPrimary }}>Ünvan:</b> {company.contactPerson} / {company.companyName}</div>
                  <div><b style={{ color: textPrimary }}>Adres:</b> {company.addressLine}</div>
                  <div><b style={{ color: textPrimary }}>Telefon:</b> {company.phoneDisplay}</div>
                  <div><b style={{ color: textPrimary }}>E-posta:</b> {company.mailDisplay}</div>
                  <div><b style={{ color: textPrimary }}>Web Sitesi:</b> {company.website}</div>
                  <div><b style={{ color: textPrimary }}>MERSİS No:</b> </div>
                </div>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  ALICI <span className="text-xs font-semibold" style={{ color: textSecondary }}>(“ALICI”)</span>
                </div>
                <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
                  <div><b style={{ color: textPrimary }}>Adı Soyadı/Unvan:</b> [Siparişte Belirtilecek]</div>
                  <div><b style={{ color: textPrimary }}>Teslimat Adresi:</b> [Siparişte Belirtilecek]</div>
                  <div><b style={{ color: textPrimary }}>Telefon:</b> [Siparişte Belirtilecek]</div>
                  <div><b style={{ color: textPrimary }}>E-posta:</b> [Siparişte Belirtilecek]</div>
                </div>
              </div>
            </div>

            <div className="mt-4 text-sm leading-7" style={{ color: textSecondary }}>
              İşbu sözleşmeyi kabul etmekle ALICI, sözleşme konusu siparişi onayladığı takdirde sipariş konusu bedeli ve
              varsa kargo ücreti, vergi gibi belirtilen ek ücretleri ödeme yükümlülüğü altına gireceğini ve bu konuda
              bilgilendirildiğini peşinen kabul eder.
            </div>
          </section>

          {/* 2 */}
          <section id="m2" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              2. TANIMLAR
            </h2>
            <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              (Bu bölüm aynen korunmuştur, yasal tanımlar için önemlidir.) <br />
              ... (Kanun, Yönetmelik, Satıcı, Alıcı, Site, Sipariş Veren, Taraflar, Sözleşme, Mal tanımları burada yer alacak) ...
            </div>
          </section>

          {/* 3 */}
          <section id="m3" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              3. KONU
            </h2>
            <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              İşbu Sözleşme, ALICI’nın, SATICI’ya ait <b style={{ color: textPrimary }}>{company.website}</b> internet sitesi üzerinden
              elektronik ortamda siparişini verdiği aşağıda nitelikleri ve satış fiyatı belirtilen 3D yazıcı, filament, aksesuar ve ilgili ürünlerin
              satışı ve teslimi ile ilgili olarak 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmelere Dair Yönetmelik
              hükümleri gereğince tarafların hak ve yükümlülüklerini düzenler.
              <div className="mt-3">
                Listelenen ve sitede ilan edilen fiyatlar satış fiyatıdır. İlan edilen fiyatlar ve vaatler güncelleme yapılana ve değiştirilene kadar geçerlidir.
                Süreli olarak ilan edilen fiyatlar ise belirtilen süre sonuna kadar geçerlidir.
              </div>
            </div>
          </section>

          {/* 4-6 kısa */}
          <section className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              4. / 5. / 6. BİLGİLER
            </h2>
            <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              4. SATICI BİLGİLERİ: Madde 1’de belirtilmiştir. <br />
              5. ALICI BİLGİLERİ: Madde 1’de belirtilmiştir. <br />
              6. SİPARİŞ VEREN KİŞİ BİLGİLERİ: Genellikle ALICI ile aynıdır.
            </div>
          </section>

          {/* 7 */}
          <section id="m7" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              7. SÖZLEŞME KONUSU ÜRÜN BİLGİLERİ VE ÖDEME
            </h2>
            <p className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              Aşağıdaki tablo, sipariş özetini ve toplam tutarı göstermektedir.
            </p>

            {/* Table */}
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-[720px] w-full text-sm border-separate border-spacing-0">
                <thead>
                  <tr>
                    {["Ürün Açıklaması", "Adet", "Birim Fiyatı (KDV Dahil)", "Ara Toplam (TL)"].map((h) => (
                      <th
                        key={h}
                        className="text-left font-bold px-3 py-2 border-b"
                        style={{ color: textPrimary, borderColor: borderCol }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="align-top">
                      <td className="px-3 py-2 border-b" style={{ color: textSecondary, borderColor: borderCol }}>
                        {r.urun}
                      </td>
                      <td className="px-3 py-2 border-b" style={{ color: textSecondary, borderColor: borderCol }}>
                        {r.adet}
                      </td>
                      <td className="px-3 py-2 border-b" style={{ color: textSecondary, borderColor: borderCol }}>
                        {r.birim}
                      </td>
                      <td className="px-3 py-2 border-b" style={{ color: textSecondary, borderColor: borderCol }}>
                        {r.araToplam}
                      </td>
                    </tr>
                  ))}

                  <tr>
                    <td className="px-3 py-2 border-b" style={{ borderColor: borderCol }} />
                    <td className="px-3 py-2 border-b" style={{ borderColor: borderCol }} />
                    <td className="px-3 py-2 border-b font-semibold" style={{ color: textPrimary, borderColor: borderCol }}>
                      Kargo Tutarı
                    </td>
                    <td className="px-3 py-2 border-b" style={{ color: textSecondary, borderColor: borderCol }}>
                      [Kargo Tutarı] TL
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-3" />
                    <td className="px-3 py-3" />
                    <td className="px-3 py-3 font-extrabold" style={{ color: textPrimary }}>
                      TOPLAM ÖDENECEK TUTAR
                    </td>
                    <td className="px-3 py-3 font-extrabold" style={{ color: textPrimary }}>
                      [Genel Toplam] TL
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="font-bold" style={{ color: textPrimary }}>Ödeme ve Teslimat</div>
                <div className="mt-2 leading-7" style={{ color: textSecondary }}>
                  <div><b style={{ color: textPrimary }}>Ödeme Şekli:</b> [Kredi Kartı / Banka Havalesi/Eft]</div>
                  <div><b style={{ color: textPrimary }}>Teslimat Adresi:</b> [Siparişte Belirtilen Adres]</div>
                  <div><b style={{ color: textPrimary }}>Fatura Adresi:</b> [Siparişte Belirtilen Adres]</div>
                  <div><b style={{ color: textPrimary }}>Sipariş Tarihi:</b> [Sipariş Tarihi]</div>
                </div>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="font-bold" style={{ color: textPrimary }}>Kargo Süresi</div>
                <div className="mt-2 leading-7" style={{ color: textSecondary }}>
                  Tahmini Teslimat Süresi: Siparişin onaylanmasından sonra <b style={{ color: textPrimary }}>2 iş günü</b> içinde kargoya verilir.
                  Kargo firmasının taahhüt ettiği süre içinde (ortalama <b style={{ color: textPrimary }}>1-3 iş günü</b>) teslim edilir. <br />
                  Teslim Şekli: Kargo ile teslimat.
                </div>
              </div>
            </div>
          </section>

          {/* 8 */}
          <section className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              8. FATURA BİLGİLERİ
            </h2>
            <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              Fatura, siparişte ALICI tarafından belirtilen adrese, sipariş ile birlikte veya elektronik ortamda (e-fatura) gönderilecektir.
            </div>
          </section>

          {/* 9 */}
          <section id="m9" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              9. GENEL HÜKÜMLER
            </h2>

            <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              (9.1’den 9.16’ya kadar olan maddeleriniz aynen korunmuştur.)
            </div>

            <div className="mt-4 rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
              <div className="text-sm font-bold" style={{ color: textPrimary }}>9.10. (Sektöre özel not dahil)</div>
              <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
                ALICI, sözleşme konusu mal/hizmeti teslim almadan önce muayene edecek; ezik, kırık, ambalajı yırtılmış vb. hasarlı ve ayıplı
                mal/hizmeti kargo şirketinden teslim almayacaktır. Teslim alınan mal/hizmetin hasarsız ve sağlam olduğu kabul edilecektir.
                Özellikle filament rulolarının ambalajının delinmemiş ve açılmamış olmasına dikkat edilmelidir. Teslimden sonra mal/hizmetin
                özenle korunması borcu, ALICI’ya aittir. Cayma hakkı kullanılacaksa mal/hizmet kullanılmamalıdır. Fatura iade edilmelidir.
              </div>
            </div>
          </section>

          {/* 10 */}
          <section id="m10" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              10. CAYMA HAKKI
            </h2>
            <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              (10.1 ve 10.2 maddeleriniz aynen korunmuştur.)
            </div>
          </section>

          {/* 11 */}
          <section id="m11" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              11. CAYMA HAKKI KULLANILAMAYACAK ÜRÜNLER / SEKTÖRE ÖZEL DURUM
            </h2>

            <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              ALICI’nın isteği veya açıkça kişisel ihtiyaçları doğrultusunda hazırlanan mallar ile aşağıdaki ürünlerde cayma hakkı kullanılamaz:
            </div>

            <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
              <li>
                <b style={{ color: textPrimary }}>Sektörümüze Özel:</b> Açılmış, orijinal vakumlu/şeritli ambalajı bozulmuş,
                kısmen veya tamamen kullanılmış 3D filamentler.
              </li>
              <li>Müşteri tarafından özel talep üzerine kesilmiş, boyanmış veya modifiye edilmiş ürünler.</li>
              <li>Sağlık ve hijyen kuralları gereği iadesi uygun olmayan ürünler (örneğin, ağızla temas eden nozzle&apos;lar, kullanılmış filtreler).</li>
              <li>Yazılım lisansları, dijital dosyalar gibi anında teslim edilen gayrimaddi mallar.</li>
              <li>Orijinal ambalajı açılmış yazılım CD/DVD&apos;leri, kitaplar.</li>
              <li>Abonelik sözleşmesi kapsamında sağlananlar dışında, süreli yayınlar.</li>
            </ul>

            <div className="mt-3 text-xs" style={{ color: textSecondary }}>
              (Bu madde, mevcut listenizi 3D baskı sektörüne göre özelleştirir ve daha anlaşılır kılar.)
            </div>
          </section>

          {/* 12 */}
          <section className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              12. TEMERRÜT HALİ VE HUKUKİ SONUÇLARI
            </h2>
            <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              (Maddeleriniz aynen korunmuştur.)
            </div>
          </section>

          {/* 13 */}
          <section id="m13" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              13. YETKİLİ MAHKEME VE UYUŞMAZLIK ÇÖZÜMÜ
            </h2>

            <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              İşbu sözleşmeden doğan uyuşmazlıkların çözümünde Türkiye Cumhuriyeti kanunları uygulanır.
              <div className="mt-3">
                Parasal sınıra ilişkin bilgiler aşağıdadır:
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  <li>Değeri 2.000,00 TL’nin altında olan uyuşmazlıklarda ilçe tüketici hakem heyetlerine,</li>
                  <li>Değeri 3.000,00 TL’nin altında bulunan uyuşmazlıklarda il tüketici hakem heyetlerine,</li>
                  <li>Büyükşehir statüsünde bulunan illerde ise değeri 2.000,00 TL ile 3.000,00 TL arasındaki uyuşmazlıklarda il tüketici hakem heyetlerine başvuru yapılmaktadır.</li>
                </ul>
              </div>
              <div className="mt-3">
                İşbu Sözleşme ticari amaçlarla yapılmaktadır.
              </div>
              <div className="mt-3">
                <b style={{ color: textPrimary }}>Yetkili Mahkeme:</b> Anlaşmazlık halinde Adana Mahkemeleri ve İcra Daireleri yetkilidir.
              </div>
            </div>
          </section>

          {/* 14 */}
          <section className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              14. YÜRÜRLÜK
            </h2>

            <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              ALICI, Site üzerinden verdiği siparişe ait ödemeyi gerçekleştirdiğinde işbu sözleşmenin tüm şartlarını kabul etmiş sayılır.
              SATICI, siparişin tamamlanması öncesinde, işbu sözleşmenin tam metnini ALICI&apos;ya elektronik ortamda göstererek ve
              &quot;Sözleşmeyi okudum, kabul ediyorum.&quot; şeklinde onay alacak şekilde gerekli teknik düzenlemeleri yapmakla yükümlüdür.
              Sözleşme, ALICI&apos;nın onayı ile birlikte elektronik ortamda akdedilmiş sayılır ve ALICI&apos;nın kayıtlı e-posta adresine
              otomatik olarak gönderilir.
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-xs font-semibold" style={{ color: textSecondary }}>SATICI</div>
                <div className="mt-1 font-bold" style={{ color: textPrimary }}>{company.companyName}</div>
              </div>
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-xs font-semibold" style={{ color: textSecondary }}>ALICI</div>
                <div className="mt-1 font-bold" style={{ color: textPrimary }}>[Sipariş Veren Adı Soyadı/Unvanı]</div>
              </div>
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-xs font-semibold" style={{ color: textSecondary }}>TARİH</div>
                <div className="mt-1 font-bold" style={{ color: textPrimary }}>[Sipariş Onay Tarihi]</div>
              </div>
            </div>

            <div className="no-print mt-5">
              <div className="text-xs" style={{ color: textSecondary }}>
                Not: Bu sayfa bilgilendirme amaçlıdır. Sipariş akışında &quot;okudum, kabul ediyorum&quot; onayı ayrıca alınmalıdır.
              </div>
              <div className="mt-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold"
                  style={{ borderColor: borderCol, color: textPrimary, backgroundColor: cardBg }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Siteye dön
                </Link>
              </div>
            </div>
          </section>

          {/* Accent footer strip */}
          <div
            className="h-1.5 rounded-full"
            style={{
              background: `linear-gradient(90deg, ${String(accent)}, rgba(249,115,22,0.9))`,
              opacity: isDarkBg ? 0.9 : 1,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default MesafeliSatisSozlesmesi;